import { Injectable, Signal, computed, inject } from '@angular/core';
import { PagedResponse } from 'mbs-ui-shared@models';
import CustomDnsBinding, { CustomDnsBindingQueryParams } from 'mbs-ui-shared@models/CustomDnsBinding';
import CustomDnsCertificate from 'mbs-ui-shared@models/CustomDnsCertificate';
import { CustomDnsBindingsService } from 'mbs-ui-shared@services/custom-dns-bindings.service';
import { CustomDnsCertificatesService } from 'mbs-ui-shared@services/custom-dns-certificates.service';
import { Observable, concatMap, of, tap } from 'rxjs';
import { CustomDnsBindingStore } from '../store/customDnsBinding/customDnsBindingStore';
import { PagingAndDataFacade } from '../store/withPagingAndDataStoreFeature/pagingAndData.facade';

@Injectable({
  providedIn: 'root'
})
export class CustomDnsBindingFacade extends PagingAndDataFacade<CustomDnsBinding> {
  protected override store = inject(CustomDnsBindingStore);
  private customDnsBindingService = inject(CustomDnsBindingsService);
  private certificatesService = inject(CustomDnsCertificatesService);

  // Custom signals exposed directly
  get certificates(): Signal<CustomDnsCertificate[]> {
    return this.store.selectCertificates;
  }

  get certificatesLoaded(): Signal<boolean> {
    return this.store.selectLoadedCertificates;
  }

  get certificatesLoading(): Signal<boolean> {
    return this.store.selectLoadingCertificates;
  }

  readonly certificatesWhenLoaded: Signal<CustomDnsCertificate[]> = computed(() =>
    this.store.selectLoadedCertificates() ? this.store.selectCertificates() : []
  );

  getBindings(params?: CustomDnsBindingQueryParams): Observable<PagedResponse<CustomDnsBinding>> {
    return this.customDnsBindingService.get(params);
  }

  create(entity: CustomDnsBinding): Observable<CustomDnsBinding> {
    return this.customDnsBindingService.create(entity).pipe(tap(() => this.invalidateEntities()));
  }

  update(entity: CustomDnsBinding): Observable<CustomDnsBinding> {
    return this.customDnsBindingService.update(entity).pipe(tap((entity) => this.invalidateEntities(entity)));
  }

  deleteRelatedBindingsInCurrentPage(entity: CustomDnsBinding): Observable<null> {
    const bindings = this.currentPage();
    const httpBindingId = entity.httpBindingId
      ? `${entity.httpBindingId}`
      : (bindings.find((b) => b.dnsName === entity.dnsName && b.certificate === 0)?.id ?? '');
    return (httpBindingId ? this.deleteBinding(httpBindingId) : of(true)).pipe(
      concatMap(() => this.deleteBinding(entity.id)),
      tap(() => this.invalidateEntities())
    );
  }

  private deleteBinding(id: string): Observable<null> {
    return this.customDnsBindingService.deleteBinding(id);
  }

  loadCertificates(force?: boolean): void {
    this.store.loadCertificates({ force });
  }

  uploadCertificate({ certificate, password }: { certificate: unknown; password: string }): Observable<CustomDnsCertificate> {
    return this.certificatesService.uploadCertificate({ certificate, password }).pipe(tap(() => this.loadCertificates(true)));
  }

  private invalidateEntities(entity?: CustomDnsBinding): void {
    this.refreshAfterCRUD(entity);
  }
}
